import { fetchDomain } from '../../_utils/utils'
import type { PlatformUserDomainRecord } from '../../models/domain/platform-user-domain-record'
import { createAccessOwner } from './access-owner'
import type { AccessOwner, AccessOwnerDeps } from './access-owner'

/**
 * The device-level key: written before the owner was keyed by user, and still the target of the
 * legacy carry-over, which runs before the user is known. The first user to sign in takes its value
 * as their own, after which nothing reads it.
 */
export const LAST_DOMAIN_STORAGE_KEY = 'pufferfish.lastDomainId'
/** The persisted AppState blob. Older versions kept the open domain inside it as a JSON string. */
const LEGACY_STATE_STORAGE_KEY = 'pufferfish'

/** Where a user's own last used domain is kept. A browser shared by two users keeps one value each. */
export const lastDomainStorageKey = (userId: number): string => `${LAST_DOMAIN_STORAGE_KEY}.${userId}`

const toDomainId = (value: unknown): number | undefined => {
    const id = Number(value)
    return Number.isSafeInteger(id) && id > 0 ? id : undefined
}

const readLegacyDomainId = (storage: Storage): number | undefined => {
    try {
        const blob = JSON.parse(storage.getItem(LEGACY_STATE_STORAGE_KEY) ?? 'null')
        return toDomainId(JSON.parse(blob?.currentDomainJsonData ?? 'null')?.id)
    } catch {
        return undefined
    }
}

const readKey = (storage: Storage, key: string): number | undefined => {
    try {
        return toDomainId(storage.getItem(key))
    } catch {
        return undefined
    }
}

const writeKey = (storage: Storage, key: string, domainId: number): boolean => {
    try {
        storage.setItem(key, String(domainId))
        return true
    } catch {
        return false
    }
}

/** False when the value could not be removed; it is then only read again by the carry-over, which skips it once any user has a key. */
const removeKey = (storage: Storage, key: string): boolean => {
    try {
        storage.removeItem(key)
        return true
    } catch {
        return false
    }
}

/** True when any user has claimed a last used domain in this browser. Unreadable storage holds nothing a user can be given. */
const anyUserHasKey = (storage: Storage): boolean => {
    try {
        const prefix = `${LAST_DOMAIN_STORAGE_KEY}.`
        for (let i = 0; i < storage.length; i++) {
            if (storage.key(i)?.startsWith(prefix)) {
                return true
            }
        }
        return false
    } catch {
        return false
    }
}

/**
 * The domain this user had open last, from this browser's cold storage. The first user to ask after
 * the key moved from the device to the user takes the device value as their own, once; a later user
 * never gets another user's value.
 */
export function readLastUsedDomainId(userId: number, storage: Storage = window.localStorage): number | undefined {
    const own = readKey(storage, lastDomainStorageKey(userId))
    if (own !== undefined) {
        return own
    }

    const device = readKey(storage, LAST_DOMAIN_STORAGE_KEY)
    if (device === undefined) {
        return undefined
    }
    removeKey(storage, LAST_DOMAIN_STORAGE_KEY)
    writeKey(storage, lastDomainStorageKey(userId), device)
    return device
}

/**
 * Remembers the open domain for this user. Storage can be full or blocked, and the last used domain
 * is a convenience nothing depends on, so a failed write reports `false` instead of throwing.
 */
export function writeLastUsedDomainId(
    userId: number,
    domainId: number,
    storage: Storage = window.localStorage,
): boolean {
    return writeKey(storage, lastDomainStorageKey(userId), domainId)
}

/**
 * Copies the legacy value to the device-level key, before the user is known. The persisted blob is
 * rewritten without the legacy field as soon as any other persisted field changes, so the copy has
 * to happen before that. Once any user has a key of their own the legacy value has been claimed, and
 * copying it again would hand it to the next user.
 */
export function carryOverLegacyLastUsedDomain(storage: Storage = window.localStorage): void {
    if (readKey(storage, LAST_DOMAIN_STORAGE_KEY) !== undefined || anyUserHasKey(storage)) {
        return
    }

    const legacy = readLegacyDomainId(storage)
    if (legacy !== undefined) {
        writeKey(storage, LAST_DOMAIN_STORAGE_KEY, legacy)
    }
}

const realDeps: AccessOwnerDeps = {
    fetchDomain: (domainId) => fetchDomain(domainId),
    readLastUsedDomainId: (userId) => readLastUsedDomainId(userId),
    writeLastUsedDomainId: (userId, domainId) => writeLastUsedDomainId(userId, domainId),
}

let instance: AccessOwner<PlatformUserDomainRecord> | undefined

/**
 * The one access owner. It is created on first use and never replaced: the app store takes it
 * once at construction, so a second instance would leave that store reading an owner nobody else sees.
 */
export function getAccessOwner(): AccessOwner<PlatformUserDomainRecord> {
    if (!instance) {
        instance = createAccessOwner<PlatformUserDomainRecord>(realDeps)
    }
    return instance
}

/** Returns the owner to its initial state in place, so every holder of it sees the reset. */
export function resetAccessOwnerForTests(): void {
    instance?.reset()
}
